import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AvailablePaymentMethod } from "@/networking/carts.api";
import FormTextInput from "../ui/input/form-text-input";
import NativeSelect from "../ui/input/native-select";
import { useEffect, useRef, useMemo } from "react";
import { useTranslation } from "@/hooks/useTranslation";

interface CustomerDetailsFormProps {
  paymentMethod: AvailablePaymentMethod;
  onSubmit: (data: Record<string, string>) => void;
  onError?: (errors: Record<string, string>) => void;
  initialValues?: Record<string, string>;
  validationErrors?: Record<string, string[]>;
  onValidationChange?: (isValid: boolean) => void;
  onClearFieldError?: (fieldKey: string) => void;
  roundedBottom?: boolean;
}

export default function CustomerDetailsForm({
  paymentMethod,
  onSubmit,
  initialValues = {},
  validationErrors = {},
  onValidationChange,
  onClearFieldError,
  roundedBottom = false,
}: CustomerDetailsFormProps) {
  const { t } = useTranslation();
  const schema = useMemo(
    () => paymentMethod.customer_details_schema || {},
    [paymentMethod.customer_details_schema],
  );

  // build Zod schema dynamically based on customer_details_schema
  const zodSchema = useMemo(
    () =>
      z.object(
        Object.entries(schema).reduce(
          (acc, [key, field]) => {
            if (field.type === "boolean") {
              // for boolean fields, accept string "true"/"false"
              let fieldSchema: z.ZodTypeAny = z.string();
              if (field.required) {
                fieldSchema = fieldSchema.refine(
                  (val) => val === "true" || val === "false",
                  { message: `${field.label} is required` },
                );
              }
              acc[key] = fieldSchema;
            } else {
              // for string fields
              let fieldSchema: z.ZodTypeAny = z.string();
              if (field.required) {
                fieldSchema = (fieldSchema as z.ZodString).min(
                  1,
                  `${field.label} is required`,
                );
              }
              // add phone validation if field key is "phone"
              if (key.toLowerCase() === "phone") {
                fieldSchema = fieldSchema.refine(
                  (val) => {
                    const v = val as string;
                    if (!v || v.trim() === "") {
                      return !field.required;
                    }
                    return v.trim().startsWith("+");
                  },
                  {
                    message:
                      "Phone number must include country code (e.g., +1 415 555 2671)",
                  },
                );
              }
              acc[key] = fieldSchema;
            }
            return acc;
          },
          {} as Record<string, z.ZodTypeAny>,
        ),
      ),
    [schema],
  );

  type FormData = z.infer<typeof zodSchema>;

  const {
    control,
    formState: { errors },
    reset,
    setError,
    clearErrors,
  } = useForm<FormData>({
    resolver: zodResolver(zodSchema as any),
    defaultValues: initialValues,
    mode: "onChange",
  });

  useEffect(() => {
    const filteredInitialValues: Record<string, string> = {};
    Object.entries(initialValues).forEach(([key, value]) => {
      if (schema[key] && value !== undefined && value !== null) {
        filteredInitialValues[key] = String(value);
      }
    });

    const initialValuesKey = JSON.stringify(filteredInitialValues);

    if (initialValuesKey !== lastInitialValuesRef.current) {
      reset(filteredInitialValues);
      lastInitialValuesRef.current = initialValuesKey;

      if (Object.keys(filteredInitialValues).length > 0) {
        const currentOnSubmit = onSubmit;
        setTimeout(() => {
          currentOnSubmit(filteredInitialValues);
        }, 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, schema, reset]);

  const formValues = useWatch({ control });
  const previousValuesRef = useRef<FormData | null>(null);
  const previousFormValuesRef = useRef<FormData | null>(null);
  const lastInitialValuesRef = useRef<string>("");

  const hasAllRequiredFields = Object.entries(schema).every(([key, field]) => {
    if (!field.required) return true;
    const value = formValues[key];
    if (field.type === "boolean") {
      return value === "true" || value === "false";
    }
    return value !== undefined && value !== null && String(value).trim() !== "";
  });

  const hasErrors =
    Object.keys(errors).length > 0 || Object.keys(validationErrors).length > 0;
  const isFormValid = hasAllRequiredFields && !hasErrors;

  useEffect(() => {
    Object.entries(validationErrors).forEach(([key, errorMessages]) => {
      if (errorMessages && errorMessages.length > 0 && schema[key]) {
        setError(key as keyof FormData, {
          type: "server",
          message: errorMessages[0],
        });
      }
    });

    Object.entries(schema).forEach(([key, field]) => {
      const currentValue = formValues[key];
      const previousValue = previousFormValuesRef.current?.[key];
      const hasBackendError =
        validationErrors[key] && validationErrors[key].length > 0;
      const hasFrontendError = errors[key] && errors[key].type !== "server";

      if (!hasBackendError && !hasFrontendError) {
        clearErrors(key as keyof FormData);
      }

      if (
        currentValue !== previousValue &&
        !hasFrontendError &&
        hasBackendError
      ) {
        let isValid = false;

        if (field.type === "boolean") {
          isValid = currentValue === "true" || currentValue === "false";
        } else {
          const trimmedValue =
            currentValue !== undefined && currentValue !== null
              ? String(currentValue).trim()
              : "";

          if (field.required) {
            isValid = trimmedValue !== "";
            if (key.toLowerCase() === "phone" && isValid) {
              isValid = trimmedValue.startsWith("+");
            }
          } else {
            isValid =
              trimmedValue === "" ||
              (key.toLowerCase() === "phone"
                ? trimmedValue.startsWith("+")
                : true);
          }
        }

        if (isValid) {
          onClearFieldError?.(key);
        }
      }
    });

    previousFormValuesRef.current = formValues;

    onValidationChange?.(isFormValid);
  }, [
    formValues,
    errors,
    validationErrors,
    schema,
    setError,
    clearErrors,
    onClearFieldError,
    isFormValid,
    onValidationChange,
  ]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // filter out undefined values
      const filteredValues: Record<string, string> = {};
      Object.entries(formValues).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          const field = schema[key];
          if (field?.type === "boolean") {
            filteredValues[key] = value === "true" ? "true" : "false";
          } else {
            filteredValues[key] = String(value);
          }
        }
      });

      const valuesString = JSON.stringify(filteredValues);
      const previousString = JSON.stringify(previousValuesRef.current);

      if (valuesString !== previousString) {
        previousValuesRef.current = filteredValues as FormData;
        const hasValues = Object.values(filteredValues).some(
          (val) => val.trim() !== "",
        );
        if (hasValues || Object.keys(filteredValues).length > 0) {
          onSubmit(filteredValues);
        }
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [formValues, onSubmit, schema]);

  // if no schema or empty schema, don't render anything
  if (!schema || Object.keys(schema).length === 0) {
    return null;
  }

  const schemaEntries = Object.entries(schema);

  return (
    <div className="border-b border-gray-200">
      {schemaEntries.map(([fieldKey, field], index) => {
        const isPhoneField = fieldKey.toLowerCase() === "phone";
        const label = isPhoneField
          ? `${field.label} (incl. country code, e.g., +1 415 555 2671)`
          : field.label;
        const isLastField = index === schemaEntries.length - 1;
        const shouldRoundBottom = roundedBottom && isLastField;

        if (field.type === "boolean") {
          return (
            <div key={fieldKey}>
              <NativeSelect
                control={control}
                name={fieldKey}
                label={label}
                options={[
                  { id: "true", name: t("yes") },
                  { id: "false", name: t("no") },
                ]}
                placeholder={t("select_an_option", "Select an option")}
                required={field.required}
                containerStyles="w-full"
                inputStyles={`w-full ${shouldRoundBottom ? "rounded-b" : ""}`}
              />
            </div>
          );
        }

        return (
          <div key={fieldKey}>
            <FormTextInput
              control={control}
              name={fieldKey}
              label={label}
              type={field.type === "string" ? "text" : field.type}
              required={field.required}
              showErrorMsg={false}
              inputStyles={`w-full ${shouldRoundBottom ? "rounded-b" : ""}`}
              containerStyles="w-full"
            />
          </div>
        );
      })}
    </div>
  );
}
